import React from 'react';
import { PanelHeader } from '../components/PanelHeader';
import { EyeIcon } from 'lucide-react';
import styled from 'styled-components';
import { SimpleFormProvider } from '../fluidComponents/fluidForm/SimpleFormLoader';
import { MaximizeIcon, MinimizeIcon, ArrowLeft } from "lucide-react";
import { useFormInstanceDispatch, useFormInstanceSelect } from '../fluidComponents/fluidForm/FormInstance.service';
import { WorkspaceInstances } from './formInstances';
const PanelWrapper = styled.div.attrs({
    className: 'preview-panel-wrapper'
}) `
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const PanelContent = styled.div.attrs({
    className: 'preview-panel-content'
}) `
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;
const StyledButton = styled.button.attrs({
    className: 'builder-styled-button'
}) `
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  height: 32px;
  margin-right: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    border-color: #4a90e2;
    transform: scale(1.05);
    color: #4a90e2;
  }
  
  svg {
    color: inherit;
  }
`;
const MaximizeButton = styled(StyledButton) `
  padding: 8px;
  width: 32px;
  justify-content: center;
`;
const StatusBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;

  ${({ $status }) => {
    switch ($status) {
        case 'not-published':
            return `background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA;`;
        case 'published':
            return `background: #F0FDF4; color: #16A34A; border: 1px solid #BBF7D0;`;
        case 'unpublished-changes':
            return `background: #FFFBEB; color: #D97706; border: 1px solid #FDE68A;`;
    }
}}
`;
const statusConfig = {
    'not-published': { label: 'Not Published', dot: '#DC2626' },
    'published': { label: 'Published', dot: '#16A34A' },
    'unpublished-changes': { label: 'Unpublished Changes', dot: '#D97706' },
};
export const FluidFormPreview = ({ isPreviewMaximized, togglePreviewMaximized, formKey, publishStatus = 'not-published', onPublishClick }) => {
    const formInstanceId = WorkspaceInstances.previewForm;
    const form = useFormInstanceSelect((state) => state.getFluidForm(), formInstanceId);
    let currentStage = useFormInstanceSelect((state) => {
        return state?.submissionStage || "draft";
    }, formInstanceId);
    let dispatch = useFormInstanceDispatch(formInstanceId);
    let mode = useFormInstanceSelect((state) => {
        return state.getStylingConfig().layout || "form";
    }, formInstanceId);
    let style = useFormInstanceSelect((state) => {
        return state.getStylingConfig().style;
    }, formInstanceId);
    let submissionId = `${formKey}-preview`;
    const setSubmissionStage = async (stage) => {
        dispatch('setSubmissionStage', stage);
    };
    const goBack = () => {
        if (currentStage == "finalized") {
            setSubmissionStage("review");
        }
        else if (currentStage == "review") {
            setSubmissionStage("draft");
        }
    };
    const badge = (React.createElement(StatusBadge, { "$status": publishStatus, onClick: onPublishClick, title: "Go to Publish tab" },
        React.createElement("svg", { width: "6", height: "6", viewBox: "0 0 6 6", fill: "none" },
            React.createElement("circle", { cx: "3", cy: "3", r: "3", fill: statusConfig[publishStatus].dot })),
        statusConfig[publishStatus].label));
    if (!form) {
        return React.createElement(PanelWrapper, null,
            React.createElement(PanelHeader, { title: "Live Preview", icon: EyeIcon, titleExtra: badge }));
    }
    return React.createElement(PanelWrapper, { "data-tour-id": "builder-preview" },
        React.createElement(PanelHeader, { title: "Live Preview", icon: EyeIcon, titleExtra: badge, actions: React.createElement(React.Fragment, null,
                currentStage != "draft" && React.createElement(StyledButton, { onClick: goBack, title: "Go Back" },
                    React.createElement(ArrowLeft, { size: 14 }),
                    "Go Back"),
                React.createElement(MaximizeButton, { onClick: () => togglePreviewMaximized(isPreviewMaximized), title: isPreviewMaximized ? "Restore" : "Maximize Preview" }, isPreviewMaximized ? (React.createElement(MinimizeIcon, { size: 16 })) : (React.createElement(MaximizeIcon, { size: 16 })))) }),
        React.createElement(PanelContent, { className: 'h-full min-h-0' }, ["form", "chat"].includes(mode) && React.createElement(SimpleFormProvider, { submissionId: submissionId, view: mode, autosave: true, formInstance: formInstanceId })));
};
export default FluidFormPreview;
